import { SessionManager } from './SessionManager';
import type {
  EmblemAuthConfig,
  AuthSession,
  WalletAuthParams,
  AuthInitResponse,
  AuthError,
  PostMessageData,
  AuthEventMap,
  AuthEventKey,
  AuthEventHandler,
  VaultInfo,
  Hex,
  SignerVaultInfo,
  EmailSendOtpParams,
  EmailVerifyOtpParams,
  EmailLoginParams,
  OAuthProvider,
} from './types';

// Signer types only - actual implementations are dynamically imported
// to keep peer dependencies (ethers, viem, @solana/web3.js) truly optional
import type { EmblemEthersWallet } from './signers/ethers';
import type { EmblemSolanaSigner } from './signers/solana';
import type { EmblemWeb3Adapter } from './signers/web3';
import type { EmblemBitcoinSigner } from './signers/bitcoin';

export class EmblemAuthSDK {
  private readonly config: EmblemAuthConfig & {
    authUrl: string;
    apiUrl: string;
    persistSession: boolean;
  };
  private session: AuthSession | null = null;
  private pendingNonce: string | null = null;
  private readonly messageHandler: (event: MessageEvent) => void;
  private overlayEl: HTMLElement | null = null;
  private _iframeEl: HTMLIFrameElement | null = null;
  private overlayCleanup: (() => void) | null = null;
  private readonly sessionMgr: SessionManager;
  private readonly events: Partial<Record<AuthEventKey, AuthEventHandler<any>[]>> = {};
  private _cachedVaultInfo: VaultInfo | null = null;
  private readonly storageKey: string;
  private readonly visitorIdKey: string;
  private _visitorId: string | null = null;

  constructor(config: EmblemAuthConfig) {
    if (!config?.appId) {
      throw new Error('appId is required');
    }

    this.config = {
      ...config,
      authUrl: config.authUrl ?? 'https://auth.emblemvault.ai',
      apiUrl: config.apiUrl ?? 'https://api.emblemvault.ai',
      persistSession: config.persistSession ?? true,
    };
    this.storageKey = `emblem_session_${config.appId}`;
    this.visitorIdKey = `emblem_visitorId_${config.appId}`;
    this.messageHandler = this.onMessage.bind(this);
    // Only add message listener in browser environments
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.messageHandler);
    }

    this.sessionMgr = new SessionManager({
      onRefresh: async current => {
        try {
          const refreshed = await this.refreshSession();
          return refreshed || current;
        } catch {
          return current;
        }
      },
    });

    // Forward session manager events
    this.sessionMgr.on('sessionExpired', s => this.emit('sessionExpired', s));
    this.sessionMgr.on('sessionRefreshed', s => this.emit('sessionRefreshed', s));
    this.sessionMgr.on('sessionWillRefresh', info => this.emit('sessionWillRefresh', info));

    // Restore persisted session if enabled
    if (this.config.persistSession) {
      const restored = this.loadPersistedSession();
      if (restored) {
        this.hydrateSession(restored);
      }
      // Also restore visitorId
      this._visitorId = this.loadPersistedVisitorId();
    }

    // Fallback for OAuth providers that break window.opener via COOP:
    // backend can redirect to the app origin with the session in the URL hash.
    this.tryConsumeSessionFromHash();
  }

  private tryConsumeSessionFromHash(): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = String(window.location.hash || '').replace(/^#/, '');
      if (!raw) return;
      const params = new URLSearchParams(raw);
      const encoded = params.get('emblemAuthSession');
      if (!encoded) return;

      const b64 = decodeURIComponent(encoded);
      const json = decodeURIComponent(escape(atob(b64)));
      const session = JSON.parse(json) as AuthSession;

      // Basic safety: only accept if appId matches this SDK instance
      if (session?.appId && session.appId !== this.config.appId) {
        this.authDebug('consumeHash:ignored (appId mismatch)', {
          expected: this.config.appId,
          received: session.appId,
        });
        return;
      }

      this.authDebug('consumeHash:accepted', { appId: session?.appId });

      this.session = session;
      this.persistSession(session);
      this.sessionMgr.setSession(session);
      this.config.onSuccess?.(session);
      this.emit('session', session);

      // Clear hash to avoid leaking tokens in copy/paste, history, etc.
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch {
        // ignore
      }
    } catch (e) {
      this.authDebug('consumeHash:failed', { error: (e as Error)?.message || String(e) });
    }
  }

  /**
   * Authenticate with a wallet signature (programmatic/headless auth)
   * Uses the external verification endpoint for cross-origin compatibility
   */
  async authenticateWallet(params: WalletAuthParams): Promise<AuthSession | null> {
    const { network, message, signature, publicKey, address } = params;

    if (!this.config.apiUrl) {
      throw new Error('apiUrl is required for authenticateWallet');
    }
    if (!network || !message || !signature) {
      throw new Error('network, message, signature are required');
    }

    const url = `${this.config.apiUrl.replace(/\/$/, '')}/api/auth/wallet/verify-external`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appId: this.config.appId,
        network,
        message,
        signature,
        publicKey,
        address,
      }),
    });

    if (!response.ok) {
      const errorData = await this.safeJson(response);
      const error = new Error(errorData?.error || `wallet_verify_${response.status}`) as AuthError;
      error.status = response.status;
      error.payload = errorData;
      throw error;
    }

    const data = await response.json();
    const session = data?.session as AuthSession | null;

    if (session) {
      this.session = session;
      this.persistSession(session);
      this.sessionMgr.setSession(session);
      this.config.onSuccess?.(session);
      this.emit('session', session);
    }

    return session;
  }

  /**
   * Start OAuth sign-in (google/twitter) using authUrl's /auth/oauth/init proxy flow.
   * This uses the same postMessage contract as openAuthModal().
   */
  async openOAuth(provider: OAuthProvider): Promise<void> {
    if (typeof window === 'undefined') {
      throw new Error('openOAuth() must be called in a browser environment');
    }

    const origin = window.location.origin;
    this.authDebug('openOAuth:start', { provider, origin, apiUrl: this.config.apiUrl });

    // Use authUrl (registration-site host) for OAuth so callback can be proxied there.
    // getAuthInit already prefers authUrl and falls back to apiUrl if needed.
    const initData = await this.getAuthInit({ origin });

    const nonce = (initData?.nonce as string) || this.randomId();
    const state = initData?.state as string;
    this.pendingNonce = nonce;
    this.authDebug('openOAuth:init ok', {
      noncePrefix: String(nonce).slice(0, 8),
      hasState: !!state,
      statePrefix: String(state || '').slice(0, 16),
    });

    const authOrigin = this.normalizeBaseUrl(this.config.authUrl, 'authUrl');
    const url = new URL(`${authOrigin}/api/auth/oauth/init`);
    url.searchParams.set('provider', provider);
    url.searchParams.set('state', state);
    this.authDebug('openOAuth:navigate', { url: url.toString() });

    // OAuth providers frequently block being embedded in iframes.
    // Always use a popup/new tab, and fallback to same-window navigation if popups are blocked.
    const win = window.open(url.toString(), 'emblem-auth', this.popupFeatures());
    if (win) {
      try {
        win.focus();
      } catch {}
      return;
    }

    // Popup blocked: fall back to same-window navigation (works in top-level browsing contexts).
    try {
      this.authDebug('openOAuth:popup blocked, using same-window navigation');
      window.location.assign(url.toString());
    } catch {
      const error = new Error('Popup blocked. Please allow popups for this site.') as AuthError;
      this.emitError(error);
    }
  }

  /**
   * Email registration: request OTP (server will send email; optionally returns OTP in dev).
   */
  async sendEmailOtp(
    params: EmailSendOtpParams
  ): Promise<{ success: boolean; expiresAt?: string; otp?: string }> {
    const apiBase = this.config.apiUrl.replace(/\/$/, '');
    const response = await fetch(`${apiBase}/api/auth/email/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appId: params.appId ?? this.config.appId,
        email: params.email,
        password: params.password,
      }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(data?.error || `email_send_otp_${response.status}`) as AuthError;
      error.status = response.status;
      error.payload = data;
      throw error;
    }
    return data;
  }

  /**
   * Email registration: verify OTP and create a session.
   */
  async verifyEmailOtp(params: EmailVerifyOtpParams): Promise<AuthSession | null> {
    const apiBase = this.config.apiUrl.replace(/\/$/, '');
    const response = await fetch(`${apiBase}/api/auth/email/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appId: params.appId ?? this.config.appId,
        email: params.email,
        otp: params.otp,
        password: params.password,
      }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(data?.error || `email_verify_otp_${response.status}`) as AuthError;
      error.status = response.status;
      error.payload = data;
      throw error;
    }
    const session = data?.session as AuthSession | null;
    if (session) {
      this.session = session;
      this.persistSession(session);
      this.sessionMgr.setSession(session);
      this.emit('session', session);
    }
    return session;
  }

  /**
   * Email login (email + password) -> session.
   */
  async authenticateEmail(params: EmailLoginParams): Promise<AuthSession | null> {
    const apiBase = this.config.apiUrl.replace(/\/$/, '');
    const response = await fetch(`${apiBase}/api/auth/email/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appId: params.appId ?? this.config.appId,
        email: params.email,
        password: params.password,
      }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(data?.error || `email_login_${response.status}`) as AuthError;
      error.status = response.status;
      error.payload = data;
      throw error;
    }
    const session = data?.session as AuthSession | null;
    if (session) {
      this.session = session;
      this.persistSession(session);
      this.sessionMgr.setSession(session);
      this.emit('session', session);
    }
    return session;
  }

  /**
   * Authenticate with a password (for AI agents / headless auth)
   * Password is hashed with appId to create a deterministic identifier.
   * Same password = same vault, different password = different vault.
   * No email or browser required - ideal for AI agents.
   *
   * @param params.password - Must be at least 16 characters (use long random strings like API keys)
   *
   * @example
   * ```typescript
   * const session = await sdk.authenticatePassword({
   *   password: 'my-super-secret-agent-password-123'
   * });
   * ```
   */
  async authenticatePassword(params: { password: string }): Promise<AuthSession | null> {
    const authBase = this.config.authUrl.replace(/\/$/, '');
    const response = await fetch(`${authBase}/api/auth/password/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appId: this.config.appId,
        password: params.password,
      }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(data?.error || `password_verify_${response.status}`) as AuthError;
      error.status = response.status;
      error.payload = data;
      throw error;
    }
    const session = data?.session as AuthSession | null;
    if (session) {
      this.session = session;
      this.persistSession(session);
      this.sessionMgr.setSession(session);
      this.emit('session', session);
    }
    return session;
  }

  /**
   * Open the authentication modal
   */
  async openAuthModal(): Promise<void> {
    const origin = window.location.origin;
    let initData: AuthInitResponse | null = null;
    let initError: AuthError | null = null;

    try {
      initData = await this.getAuthInit({ origin });
    } catch (error) {
      initError = error as AuthError;
      console.warn('[EmblemAuthSDK] /api/auth/init failed:', initError?.message || error);
    }

    const nonce = initData?.nonce || this.randomId();
    this.pendingNonce = nonce;

    let modalUrl = this.resolveModalUrl({ nonce, origin, state: initData?.state });

    // If origin not allowed, open modal error page directly
    if (initError?.payload?.error === 'origin_not_allowed') {
      try {
        const base = new URL('/connect', this.config.authUrl);
        base.searchParams.set('error', 'origin_not_allowed');
        base.searchParams.set('appId', this.config.appId);
        base.searchParams.set('origin', origin);
        modalUrl = base.toString();
      } catch {
        // Ignore URL construction errors
      }
    }

    const mode = this.config.modalMode || 'auto';
    let opened = false;

    if (mode === 'iframe' || mode === 'auto') {
      try {
        opened = this.openIframeModal(modalUrl);
      } catch (error) {
        console.warn(
          '[EmblemAuthSDK] iframe modal failed, will fallback to popup:',
          (error as Error)?.message
        );
      }
    }

    if (!opened) {
      const features = this.popupFeatures();
      const win = window.open(modalUrl, 'emblem-auth', features);
      if (!win) {
        const error = new Error('Popup blocked. Please allow popups for this site.') as AuthError;
        this.emitError(error);
      }
    }
  }

  /**
   * Get the current session
   */
  getSession(): AuthSession | null {
    return this.session;
  }

  /**
   * Get the visitor ID from the last authentication
   * This is the VaultFP fingerprint ID captured during auth
   */
  getVisitorId(): string | null {
    return this._visitorId;
  }

  /**
   * Refresh the current session
   * Supports two modes:
   * - Web apps: Uses httpOnly cookies (credentials: include, no body)
   * - Mobile/Native apps: Uses refresh token from session (sent in body)
   */
  async refreshSession(): Promise<AuthSession | null> {
    try {
      if (!this.session) return null;

      // Extract origin from authUrl for refresh endpoint
      const authOrigin = this.tryGetOriginFromConfig();
      if (!authOrigin) return this.session;

      const refreshUrl = `${authOrigin}/api/auth/refresh`;

      // Check if we have a refresh token in session (mobile/native mode)
      // If so, send it in the body. Otherwise, rely on httpOnly cookie (web mode)
      const hasTokenInSession = Boolean(this.session.refreshToken);

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hasTokenInSession ? { refreshToken: this.session.refreshToken } : {}),
        // Always include credentials - needed for cookie mode, harmless for token mode
        credentials: 'include',
      });

      if (!response.ok) return this.session;

      const data = await response.json();
      const nextSession = data?.session as AuthSession | null;

      if (nextSession) {
        // For mobile/native: preserve the new refresh token if returned
        // For web: refreshToken will be undefined (handled by cookies)
        this.session = nextSession;
        this.persistSession(nextSession);
        this.sessionMgr.setSession(nextSession);
        this.emit('session', nextSession);
      }

      return this.session;
    } catch {
      return this.session;
    }
  }

  /**
   * Get vault information
   */
  async getVaultInfo(): Promise<VaultInfo> {
    if (this._cachedVaultInfo) return this._cachedVaultInfo;

    const session = this.getSession();
    if (!session?.authToken) {
      throw new Error('No session');
    }

    if (!this.config.apiUrl) {
      throw new Error('apiUrl is required');
    }

    const apiBase = this.config.apiUrl.replace(/\/$/, '');
    const resp = await fetch(`${apiBase}/vault/info`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.authToken}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    });

    if (!resp.ok) {
      throw new Error('Failed to fetch vault info');
    }

    const rawVaultInfo = await resp.json();

    // Normalize the response to match the VaultInfo type
    const vaultInfo: VaultInfo = {
      ...rawVaultInfo,
      // Ensure tokenId is populated (alias for vaultId)
      tokenId: rawVaultInfo.tokenId || rawVaultInfo.vaultId,
      // Populate solanaAddress from address if not already set
      // The 'address' field from the API is the Solana address
      solanaAddress: rawVaultInfo.solanaAddress || rawVaultInfo.address,
    };

    this._cachedVaultInfo = vaultInfo;
    return vaultInfo;
  }

  /**
   * Get or create the vault API key
   */
  async getVaultApiKey(): Promise<string> {
    if (!this.config.apiUrl) {
      throw new Error('apiUrl is required');
    }

    const session = this.getSession();
    if (!session?.authToken) {
      throw new Error('No active session');
    }

    const apiBase = this.config.apiUrl.replace(/\/$/, '');
    const token = session.authToken;
    const vaultId = session.user?.vaultId;

    // Try to get API key hash from vault info
    let apiKeyHash: string | null = null;

    try {
      const infoRes = await fetch(`${apiBase}/vault/info-complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (infoRes.ok) {
        const infoJson = await infoRes.json();
        apiKeyHash = infoJson?.raw?.pkp?.api_key_hash || infoJson?.raw?.pkp?.apiKeyHash || null;
      }
    } catch {
      // Ignore errors
    }

    // Fallback: list by created_by if info-complete didn't return a hash
    if (!apiKeyHash) {
      try {
        const appId = session.appId;
        const rawIdentifier = session.user?.identifier || '';
        const scopedIdentifier = appId ? `${appId}:${rawIdentifier}` : rawIdentifier;

        if (scopedIdentifier) {
          const vaultsRes = await fetch(
            `${apiBase}/api/vaults/${encodeURIComponent(scopedIdentifier)}`
          );
          if (vaultsRes.ok) {
            const vaults = await vaultsRes.json();
            const match = Array.isArray(vaults)
              ? vaults.find(v => String(v.tokenId || v.token_id) === String(vaultId)) || vaults[0]
              : null;
            apiKeyHash = match?.api_key_hash || match?.apiKeyHash || null;
          }
        }
      } catch {
        // Ignore errors
      }
    }

    // If no hash, generate a new key
    if (!apiKeyHash) {
      const genRes = await fetch(`${apiBase}/api/vaults/${encodeURIComponent(vaultId)}/api-key`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!genRes.ok) {
        throw new Error(`gen_key_${genRes.status}`);
      }

      const genJson = await genRes.json();
      const apiKey = genJson?.apiKey || genJson?.key || null;

      if (!apiKey) {
        throw new Error('missing_apiKey');
      }

      return apiKey;
    }

    // Decrypt existing key
    const decRes = await fetch(`${apiBase}/decrypt`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tokenId: vaultId, dataToEncryptHash: apiKeyHash }),
    });

    if (!decRes.ok) {
      throw new Error(`decrypt_${decRes.status}`);
    }

    const decJson = await decRes.json();
    const decrypted = decJson?.decryptedString || null;

    if (!decrypted) {
      throw new Error('missing_decrypted');
    }

    return decrypted;
  }

  /**
   * Hydrate a previously saved session
   */
  hydrateSession(session: AuthSession): void {
    if (!session?.authToken) return;

    this.session = session;
    this.persistSession(session);
    this._cachedVaultInfo = null; // Clear cache on session change
    this.sessionMgr.setSession(session);
    this.emit('session', session);
  }

  /**
   * Logout and clear the session
   */
  logout(): void {
    this.session = null;
    this.persistSession(null);
    this._visitorId = null;
    this.persistVisitorId(null);
    this._cachedVaultInfo = null; // Clear cache on logout
    this.sessionMgr.clear();
    this.emit('session', null);
  }

  /**
   * Subscribe to auth events
   */
  on<K extends AuthEventKey>(event: K, handler: AuthEventHandler<K>): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event]!.push(handler as any);
  }

  /**
   * Unsubscribe from auth events
   */
  off<K extends AuthEventKey>(event: K, handler: AuthEventHandler<K>): void {
    const handlers = this.events[event];
    if (handlers) {
      const index = handlers.indexOf(handler as any);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  // ============================================================================
  // Signer Adapters
  // ============================================================================

  /**
   * Helper to get signer config and vault info
   */
  private async getSignerContext(): Promise<{
    config: { baseUrl?: string; getJwt: () => string | null | undefined };
    vaultInfo: SignerVaultInfo;
  }> {
    const session = this.getSession();
    if (!session?.authToken) {
      throw new Error('No active session. Call openAuthModal() or authenticateWallet() first.');
    }

    const vaultInfo = await this.getVaultInfo();

    return {
      config: {
        baseUrl: this.config.apiUrl,
        getJwt: () => this.getSession()?.authToken,
      },
      vaultInfo: {
        vaultId: vaultInfo.vaultId,
        evmAddress: (vaultInfo.evmAddress || '0x') as Hex,
        address: vaultInfo.solanaAddress || vaultInfo.address || '',
        tokenId: vaultInfo.tokenId,
        created_by: vaultInfo.created_by,
      },
    };
  }

  /**
   * Create a viem-compatible account adapter for EVM signing
   * Requires an active session with authToken
   *
   * @example
   * ```typescript
   * const account = await sdk.toViemAccount();
   * const signature = await account.signMessage({ message: 'Hello' });
   * ```
   */
  async toViemAccount(): Promise<{
    address: Hex;
    signMessage: (args: { message: unknown }) => Promise<Hex>;
    signTypedData: (typedData: unknown) => Promise<Hex>;
    signTransaction: (tx: unknown, opts?: unknown) => Promise<Hex>;
    type: 'local';
    source: 'custom';
  }> {
    const { toViemAccount } = await import('./signers/viem');
    const { config, vaultInfo } = await this.getSignerContext();
    return toViemAccount(config, vaultInfo);
  }

  /**
   * Create an ethers v6 compatible wallet adapter for EVM signing
   * Requires an active session with authToken
   *
   * @param provider - Optional ethers Provider for transaction population
   *
   * @example
   * ```typescript
   * const wallet = await sdk.toEthersWallet(provider);
   * const signature = await wallet.signMessage('Hello');
   * const tx = await wallet.sendTransaction({ to, value });
   * ```
   */
  async toEthersWallet(provider?: unknown | null): Promise<EmblemEthersWallet> {
    const { toEthersWallet } = await import('./signers/ethers');
    const { config, vaultInfo } = await this.getSignerContext();
    return toEthersWallet(config, provider, vaultInfo);
  }

  /**
   * Create a Web3.js compatible adapter for EVM signing
   * Requires an active session with authToken
   *
   * @example
   * ```typescript
   * const adapter = await sdk.toWeb3Adapter();
   * const signature = await adapter.signMessage('Hello');
   * ```
   */
  async toWeb3Adapter(): Promise<EmblemWeb3Adapter> {
    const { toWeb3Adapter } = await import('./signers/web3');
    const { config, vaultInfo } = await this.getSignerContext();
    return toWeb3Adapter(config, vaultInfo);
  }

  /**
   * Create a Solana Web3.js compatible signer
   * Requires an active session with authToken
   *
   * @example
   * ```typescript
   * const signer = await sdk.toSolanaWeb3Signer();
   * const signature = await signer.signMessage('Hello');
   * const signedTx = await signer.signTransaction(tx);
   * ```
   */
  async toSolanaWeb3Signer(): Promise<EmblemSolanaSigner> {
    const { toSolanaWeb3Signer } = await import('./signers/solana');
    const { config, vaultInfo } = await this.getSignerContext();
    return toSolanaWeb3Signer(config, vaultInfo);
  }

  /**
   * Create a Solana Kit compatible signer
   * Requires an active session with authToken
   *
   * @example
   * ```typescript
   * const signer = await sdk.toSolanaKitSigner();
   * const signature = await signer.signMessage('Hello');
   * ```
   */
  async toSolanaKitSigner(): Promise<EmblemSolanaSigner> {
    const { toSolanaKitSigner } = await import('./signers/solana');
    const { config, vaultInfo } = await this.getSignerContext();
    return toSolanaKitSigner(config, vaultInfo);
  }

  /**
   * Create a Bitcoin signer for PSBT signing
   * Requires an active session with authToken
   *
   * @example
   * ```typescript
   * const signer = await sdk.toBitcoinSigner();
   * console.log('Bitcoin addresses:', signer.addresses);
   *
   * // Sign a PSBT
   * const result = await signer.signPsbt(psbtBase64, {
   *   transactionType: 'p2wpkh'
   * });
   * console.log('Signed TX:', result.signedTxHex);
   * ```
   */
  async toBitcoinSigner(): Promise<EmblemBitcoinSigner> {
    const { toBitcoinSigner, fetchBitcoinVaultInfo } = await import('./signers/bitcoin');
    const { config } = await this.getSignerContext();
    // Bitcoin signer needs its own vault info with btcPubkey
    const btcVaultInfo = await fetchBitcoinVaultInfo(config);
    return toBitcoinSigner(config, btcVaultInfo);
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    // Only remove message listener in browser environments
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.messageHandler);
    }
    this.sessionMgr.destroy();
    this.closeOverlay();
    Object.keys(this.events).forEach(key => {
      delete this.events[key as AuthEventKey];
    });
  }

  private resolveModalUrl(params: { nonce: string; origin: string; state?: string }): string {
    // Derive modal URL from authUrl
    const base = `${this.config.authUrl.replace(/\/$/, '')}/connect`;
    const url = new URL(base, window.location.href);
    url.searchParams.set('appId', this.config.appId);
    url.searchParams.set('origin', params.origin);
    url.searchParams.set('nonce', params.nonce);
    if (params.state) {
      url.searchParams.set('state', params.state);
    }
    return url.toString();
  }

  private authDebugEnabled(): boolean {
    // Browser: allow enabling via localStorage.AUTH_DEBUG="true"
    try {
      const v = window?.localStorage?.getItem?.('AUTH_DEBUG');
      if (String(v).toLowerCase() === 'true') return true;
    } catch {
      // ignore
    }

    // Bundler envs: allow NEXT_PUBLIC_AUTH_DEBUG / AUTH_DEBUG if present
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const p: any = typeof process !== 'undefined' ? process : null;
      const env = p?.env || {};
      const v = env.NEXT_PUBLIC_AUTH_DEBUG ?? env.AUTH_DEBUG;
      if (!v) return false;
      const s = String(v).toLowerCase().trim();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    } catch {
      return false;
    }
  }

  private authDebug(...args: unknown[]): void {
    if (!this.authDebugEnabled()) return;
    // eslint-disable-next-line no-console
    console.log('[AUTH_DEBUG]', ...args);
  }

  /**
   * Normalize a configured URL. Accepts absolute URLs (https://...) or relative paths (/...).
   * If a host-like string is provided without a scheme (e.g. "api.emblemvault.ai"),
   * we assume https://.
   */
  private normalizeBaseUrl(input: string, label: 'apiUrl' | 'authUrl'): string {
    if (!input) throw new Error(`${label} is required`);
    const trimmed = String(input).trim();
    const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost';

    // First attempt: standard URL parsing (supports relative paths in browser)
    try {
      const u = new URL(trimmed, base);
      return u.toString().replace(/\/$/, '');
    } catch {
      // continue
    }

    // Second attempt: treat as host-like and assume https://
    try {
      const u = new URL(`https://${trimmed.replace(/^\/+/, '')}`);
      return u.toString().replace(/\/$/, '');
    } catch {
      // continue
    }

    throw new Error(
      `${label} must be an absolute URL (e.g. "https://api.emblemvault.ai") or a relative path (e.g. "/")`
    );
  }

  private onMessage(event: MessageEvent): void {
    try {
      // Validate origin
      const allowedOrigins = new Set(
        [
          window.location.origin,
          this.tryGetOriginFromConfig(),
          this.tryGetOriginFromApiUrl(),
        ].filter(Boolean)
      );

      if (!allowedOrigins.has(event.origin)) {
        this.authDebug('onMessage:ignored (origin)', {
          eventOrigin: event.origin,
          allowedOrigins: Array.from(allowedOrigins),
        });
        return;
      }

      const data = event.data as PostMessageData;

      // Handle cancel message
      if (data?.type === 'emblem-auth-cancelled') {
        // Nonce check for cancel
        if (!this.pendingNonce || data.nonce !== this.pendingNonce) {
          return;
        }

        this.pendingNonce = null;
        this.closeOverlay();

        // Call onCancel callback if provided
        if (this.config.onCancel) {
          this.config.onCancel();
        }

        // Emit cancelled event
        this.emit('cancelled', undefined as never);
        return;
      }

      if (data?.type !== 'emblem-auth-success') return;

      // Nonce check
      if (!this.pendingNonce || data.nonce !== this.pendingNonce) {
        console.error('[EmblemAuthSDK] Nonce mismatch', {
          expected: this.pendingNonce,
          received: data?.nonce,
          origin: event.origin,
        });
        this.authDebug('onMessage:ignored (nonce)', {
          expected: this.pendingNonce,
          received: data?.nonce,
          origin: event.origin,
          type: data?.type,
        });
        return;
      }

      this.pendingNonce = null;
      this.authDebug('onMessage:accepted', { origin: event.origin });

      // Accept the session
      const session = data.session as AuthSession | null;
      this.session = session;
      this.persistSession(session);
      this.sessionMgr.setSession(session);

      // Store visitorId if provided
      if (data.visitorId) {
        this._visitorId = data.visitorId;
        this.persistVisitorId(data.visitorId);
      }

      if (session && this.config.onSuccess) {
        this.config.onSuccess(session);
      }

      this.emit('session', session);
      this.closeOverlay();
    } catch (error) {
      this.emitError(error as AuthError);
    }
  }

  private emit<K extends AuthEventKey>(event: K, payload: Parameters<AuthEventMap[K]>[0]): void {
    const handlers = this.events[event];
    if (!handlers) return;

    for (const handler of handlers) {
      try {
        handler(payload as any);
      } catch (error) {
        console.error(`Error in auth event handler for ${event}:`, error);
      }
    }
  }

  private emitError(error: AuthError): void {
    this.config.onError?.(error);
    this.emit('authError', error);
  }

  private popupFeatures(): string {
    // Slightly wider for better UX, still reasonable on small screens
    const width = 520;
    const height = 680;
    const top = Math.max(0, (window.outerHeight - height) / 2);
    const left = Math.max(0, (window.outerWidth - width) / 2);
    return `popup=yes,width=${width},height=${height},left=${left},top=${top}`;
  }

  private randomId(): string {
    const array = new Uint8Array(16);
    if (window.crypto?.getRandomValues) {
      window.crypto.getRandomValues(array);
    } else {
      for (let i = 0; i < array.length; i++) {
        array[i] = Math.floor(Math.random() * 256);
      }
    }
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  private tryGetOriginFromConfig(): string | null {
    try {
      if (!this.config.authUrl) return null;
      // Use window.location.href as base in browser (supports relative authUrl)
      // In Node.js, authUrl must be absolute
      const normalized = this.normalizeBaseUrl(this.config.authUrl, 'authUrl');
      return new URL(normalized).origin;
    } catch {
      return null;
    }
  }

  private tryGetOriginFromApiUrl(): string | null {
    try {
      if (!this.config.apiUrl) return null;
      const normalized = this.normalizeBaseUrl(this.config.apiUrl, 'apiUrl');
      return new URL(normalized).origin;
    } catch {
      return null;
    }
  }

  private async getAuthInit(params: { origin: string }): Promise<AuthInitResponse> {
    const body = JSON.stringify({
      appId: this.config.appId,
      origin: params.origin,
    });

    const tryInit = async (baseUrl: string): Promise<AuthInitResponse> => {
      const response = await fetch(`${baseUrl.replace(/\/$/, '')}/api/auth/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const error = new Error(`init ${response.status}`) as AuthError;
        error.status = response.status;
        error.payload = data || {};
        throw error;
      }

      return data as AuthInitResponse;
    };

    // Prefer authUrl (registration site) for single-origin setups, but fallback to apiUrl
    // so modal can still open with a valid state even if the authUrl proxy route isn't deployed.
    try {
      return await tryInit(this.config.authUrl);
    } catch (error) {
      const firstError = error as AuthError;
      try {
        return await tryInit(this.config.apiUrl);
      } catch (fallbackError) {
        // Preserve original failure details (often more actionable), but include fallback in payload.
        const merged = firstError;
        merged.payload = {
          ...(firstError?.payload || {}),
          fallback: (fallbackError as AuthError)?.payload || undefined,
        };
        throw merged;
      }
    }
  }

  private openIframeModal(url: string): boolean {
    // Clean up any existing overlay
    if (this.overlayEl) {
      this.closeOverlay();
    }

    // Create overlay
    const overlay = document.createElement('div');
    overlay.setAttribute('data-emblem-overlay', '');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.background = 'rgba(0,0,0,0.55)';
    // Backdrop blur where supported (nice polish)
    (overlay.style as any).backdropFilter = 'blur(6px)';
    (overlay.style as any).webkitBackdropFilter = 'blur(6px)';
    overlay.style.zIndex = '999999';
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.padding = '12px';

    // Create container
    const container = document.createElement('div');
    container.style.width = 'min(520px, 96vw)';
    container.style.height = 'min(680px, 92vh)';
    container.style.background = '#12161b';
    container.style.border = '1px solid #222b35';
    container.style.borderRadius = '12px';
    container.style.boxShadow = '0 12px 48px rgba(0,0,0,0.7)';
    container.style.overflow = 'hidden';
    container.style.position = 'relative';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';

    // Header bar (keeps close button from overlapping iframe content)
    const header = document.createElement('div');
    header.style.height = '52px';
    header.style.display = 'flex';
    header.style.alignItems = 'center';
    header.style.justifyContent = 'space-between';
    header.style.padding = '0 12px';
    header.style.borderBottom = '1px solid rgba(255, 255, 255, 0.06)';
    header.style.background = '#12161b';

    const title = document.createElement('div');
    title.textContent = 'Emblem';
    title.style.fontFamily =
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial';
    title.style.fontSize = '13px';
    title.style.letterSpacing = '0.08em';
    title.style.textTransform = 'uppercase';
    title.style.color = 'rgba(230, 238, 248, 0.75)';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = '×';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.setAttribute('title', 'Close');
    closeBtn.style.width = '40px';
    closeBtn.style.height = '40px';
    closeBtn.style.display = 'flex';
    closeBtn.style.alignItems = 'center';
    closeBtn.style.justifyContent = 'center';
    closeBtn.style.background = 'rgba(255, 255, 255, 0.04)';
    closeBtn.style.color = '#e6eef8';
    closeBtn.style.border = '1px solid rgba(255, 255, 255, 0.08)';
    closeBtn.style.borderRadius = '10px';
    closeBtn.style.fontSize = '24px';
    closeBtn.style.lineHeight = '1';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.userSelect = 'none';
    closeBtn.onclick = () => this.cancelAuth();

    header.appendChild(title);
    header.appendChild(closeBtn);

    // Create iframe
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = '0';
    iframe.referrerPolicy = 'no-referrer';
    iframe.allow = 'clipboard-read; clipboard-write;';

    container.appendChild(header);
    container.appendChild(iframe);
    overlay.appendChild(container);
    document.body.appendChild(overlay);

    // Close when clicking outside the modal content
    overlay.addEventListener('click', e => {
      if (e.target === overlay) this.cancelAuth();
    });

    // Close on Escape
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        this.cancelAuth();
      }
    };
    document.addEventListener('keydown', onKey, { capture: true });

    this.overlayEl = overlay;
    this._iframeEl = iframe;
    this.overlayCleanup = () => {
      document.removeEventListener('keydown', onKey, { capture: true });
    };

    return true;
  }

  private closeOverlay(): void {
    try {
      this.overlayCleanup?.();
    } catch {
      // Ignore errors
    }
    this.overlayCleanup = null;

    if (this.overlayEl?.parentNode) {
      this.overlayEl.parentNode.removeChild(this.overlayEl);
    }

    this.overlayEl = null;
    this._iframeEl = null;
  }

  /**
   * Cancel authentication (called when user closes modal via X button or Escape)
   */
  private cancelAuth(): void {
    this.pendingNonce = null;
    this.closeOverlay();

    // Call onCancel callback if provided
    if (this.config.onCancel) {
      this.config.onCancel();
    }

    // Emit cancelled event
    this.emit('cancelled', undefined as never);
  }

  private async safeJson(response: Response): Promise<any> {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  private loadPersistedSession(): AuthSession | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return null;

      const session = JSON.parse(stored) as AuthSession;

      // Validate session has required fields and hasn't expired
      if (!session?.authToken || !session?.expiresAt) return null;
      if (Date.now() >= session.expiresAt) {
        localStorage.removeItem(this.storageKey);
        return null;
      }

      return session;
    } catch {
      return null;
    }
  }

  private persistSession(session: AuthSession | null): void {
    try {
      if (typeof localStorage === 'undefined') return;

      if (session && this.config.persistSession) {
        localStorage.setItem(this.storageKey, JSON.stringify(session));
      } else {
        localStorage.removeItem(this.storageKey);
      }
    } catch {
      // Ignore storage errors (e.g., private browsing mode)
    }
  }

  private loadPersistedVisitorId(): string | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      return localStorage.getItem(this.visitorIdKey);
    } catch {
      return null;
    }
  }

  private persistVisitorId(visitorId: string | null): void {
    try {
      if (typeof localStorage === 'undefined') return;

      if (visitorId && this.config.persistSession) {
        localStorage.setItem(this.visitorIdKey, visitorId);
      } else {
        localStorage.removeItem(this.visitorIdKey);
      }
    } catch {
      // Ignore storage errors (e.g., private browsing mode)
    }
  }
}
